import { html, TemplateResult } from 'lit';
import { filter, isArguments, isBuffer, uniqueId } from 'lodash';
import {
  DisplayRow,
  getSimulationQueryInput,
  getSearchParameters,
  ManufacturerFilter,
  runSimulationSearch,
  saveSimulationQueryInput,
  SearchItem,
  SearchItemManufacturer,
  SimulationSearchPage,
  runSimulationCSV,
  SearchItemBase,
  SimulationQueryInput,
  SearchParameterFilter,
  SearchItemFabricator,
  getFavourites,
  Favourite,
  setFavourites,
  setJobItems,
  getJobItems,
} from '../data/search-data';
import { BasicJobView } from './basic-job-view';
import { FavouritesView } from './favourites-view';
import { JobView } from './job-view';

import { ViewBase } from './view-base';
import { newGuid } from './view-consts';

const $j = $.fn as any;

interface ScatterPoint {
  x: number;
  y: number;
  item: DisplayRow;
}
interface ScatterGraphData {
  label: string;
  data: ScatterPoint[];
  pointStyle?: string;
  pointRadius?: number;
  borderWidth?: number;
  pointBorderColor?: string;

  backgroundColor: string;
}

const scattergraphBackgroundColors = [
  'rgb(255, 0, 0)',
  'rgb(255, 255, 0)',
  'rgb(0, 255, 0)',
  'rgb(0, 128, 0)',
  'rgb(0, 0, 255)',
  'rgb(255, 0, 255)',
  'rgb(0, 0, 0)',
  'rgb(128, 128, 128)',
  'rgb(128, 0, 0)',
  'rgb(128, 128, 0)',
  'rgb(0, 255, 255)',
  'rgb(0, 128, 128)',
  'rgb(0, 0, 128)',
  'rgb(128, 0, 128)',
  'rgb(211, 84, 0)',
  'rgb(231, 76, 60)',
  'rgb(26, 188, 156)',
  'rgb(155, 89, 182)',
  'rgb(35, 155, 86)',
  'rgb(33, 97, 140)',
  'rgb(244, 208, 63)',
  'rgb(20, 90, 50)',
  'rgb(46, 64, 83)',
  'rgb(40, 180, 99)',
];

const tabs = {
  graph: 'graph-data-tab',
  table: 'table-data-tab',
  favourites: 'fav-data-tab',
  job: 'job-data-tab',
  basicjob: 'basicjob-data-tab',
};

const activeTabSetting = 'wers-portal-search:activeTab';
export class SearchView extends ViewBase {
  searchPage?: SimulationSearchPage;
  graphSearchPage?: SimulationSearchPage;
  elementId?: string;
  pageSize: number = 200;
  sortField: string = 'windowId';
  sortAsc: boolean = true;
  currentPageIndex?: number;
  scatterChart: any | null = null;
  chartCtx: any | null = null;
  activeTab: string;
  changeEvent: number = 0;
  dataTable: any | null = null;
  firstRenderOccurred: boolean = false;
  timer: NodeJS.Timer | null = null;
  currentGraphItem?: DisplayRow;
  graphAggregateValue: string | null = null;
  private select2ChangeEvent: any;
  private searchDrawOpen: boolean = true;
  private rebuildGraphData: boolean = true;

  favouritesView: FavouritesView;
  jobItemView: JobView;
  basicJobItemView: BasicJobView;
  graphData: any;
  constructor(name: string, globalRenderEvent: () => Promise<void>) {
    super(name, globalRenderEvent);
    this.changeEvent++;
    try {
      this.activeTab = sessionStorage.getItem(activeTabSetting) ?? tabs.table;
      this.jobItemView = new JobView(() => this.jobItemsChanged());
      this.basicJobItemView = new BasicJobView(() =>
        this.jobItemsChanged(true)
      );
      this.favouritesView = new FavouritesView(
        () => this.favouritesChanged(),
        (dr: DisplayRow) => this.addJob(dr),
        (windowId: string) => this.basicJobItemView.hasJobItem(windowId)
      );
      this.select2ChangeEvent = async (e) => {
        const jthis = this.$();
        if (e.currentTarget.id === 'input-state' && this.changeEvent === 0) {
          //await this.filterValueChanged();
          this.searchPage = this.updateSearchPage();

          //render required to update manufacturer
          await this.render();

          return;
        }

        const setExclusiveStatesOnLookups = () => {
          const manufacturerLookup = jthis.find('#input-manufacturer');
          const fabricatorLookup = jthis.find('#input-fabricator');
          const manVal = this.getListValues(jthis, 'manufacturer');
          const fabVal = this.getListValues(jthis, 'fabricator');
          if (manVal.length > 0) fabricatorLookup.prop('disabled', true);
          else fabricatorLookup.prop('disabled', false);
          if (fabVal.length > 0) manufacturerLookup.prop('disabled', true);
          else manufacturerLookup.prop('disabled', false);
        };

        setExclusiveStatesOnLookups();
        if (this.changeEvent > 0) return;
        this.searchPage = this.updateSearchPage();

        // await this.filterValueChanged();
      };

      this.favouritesView.setFavourites(getFavourites());
      this.jobItemView.setJobItems(getJobItems());
      this.basicJobItemView.setJobItems(getJobItems(true));
    } finally {
      this.changeEvent--;
    }
  }
  jobItemsChanged(basic?: boolean): void {
    if (basic) {
      setJobItems(this.basicJobItemView.jobitems, basic);
    } else {
      setJobItems(this.jobItemView.jobitems, basic);
    }
    this.render();
  }
  addJob(dr: DisplayRow): void {
    this.basicJobItemView.toggleJob(dr);
  }
  favouritesChanged(): void {
    setFavourites(this.favouritesView.favourites);
    if (this.changeEvent === 0) this.render();
  }

  protected get manufacturerFilter(): ManufacturerFilter {
    return ManufacturerFilter.residential;
  }

  protected async onPageActivated(active: Boolean) {
    if (active) {
      if (!this.searchPage) {
        const input = getSimulationQueryInput(this.manufacturerFilter ?? 1);
        if (input) this.searchPage = { input: input };
      }
      if (!this.graphSearchPage && this.searchPage) {
        this.graphSearchPage = { input: this.searchPage.input };
      }
      this.firstRenderOccurred = false;
    } else {
      if (this.scatterChart) {
        this.scatterChart.destroy();
        this.scatterChart = null;
      }
      if (this.dataTable) {
        this.dataTable.destroy();
        this.dataTable = null;
      }

      const jthis = this.$();
      this.changeEvent++;
      try {
        jthis
          .find('.use-select2')
          .each((index: number, elem: HTMLSelectElement) => {
            $(elem).off('change', this.select2ChangeEvent);
          });
      } finally {
        this.changeEvent--;
      }
    }
  }

  protected doConstruct() {
    super.doConstruct();
    this.elementId = `simulation-search-${this.uiid}`;
  }

  private elemId(name: string) {
    return name + '-' + this.uiid;
  }
  public async template(): Promise<TemplateResult> {
    const elemId = (name: string) => this.elemId(name);
    const date = new Date();
    const params = await getSearchParameters();
    const filters = params?.cache;

    //get the correct lookups for the page
    const lookups = this.activePageLookups(elemId);

    //return true if a state is currently in the selected filter.
    //this is used by the manufacturer list and fabricator list.
    let stateInFilter = (stateIds: number[]) => {
      if (!lookups) return true;
      if (lookups.addressState.length === 0) return true;

      return stateIds.some((i) =>
        lookups.addressState.some((x) => x.id === i.toString())
      );
    };

    //build the current list of manufacturers based on type and state.
    const manufacturerList =
      (params?.cache.manufacturer as SearchItemManufacturer[]).filter(
        (x) =>
          (this.manufacturerFilter === null ||
            (x.isCommercial &&
              this.manufacturerFilter == ManufacturerFilter.commercial) ||
            (x.isResidential &&
              this.manufacturerFilter == ManufacturerFilter.residential)) &&
          stateInFilter(x.stateIds)
      ) ?? [];

    //build the current list of fabricators based on type and state.
    const fabricatorList =
      (params?.cache.fabricator as SearchItemFabricator[]).filter(
        (x) =>
          (this.manufacturerFilter === null ||
            (x.isCommercial &&
              this.manufacturerFilter == ManufacturerFilter.commercial) ||
            (x.isResidential &&
              this.manufacturerFilter == ManufacturerFilter.residential)) &&
          stateInFilter(x.stateIds)
      ) ?? [];

    const optionTemplate = (x: SearchItemManufacturer): TemplateResult => {
      return html`<option .value=${x.id}>
        ${this.fallback(x.description, x.code)}
      </option>`;
    };

    const header = () => {
      if (this.manufacturerFilter != null) {
        if (this.manufacturerFilter == ManufacturerFilter.commercial)
          return html`<h4>Commercial Search</h4>`;
        else return html`<h4>Residential Search</h4>`;
      }
      return html``;
    };

    //generic list filter function for all the selection lists.
    const containsId = (
      id: string,
      list: SearchItemBase[] | null | undefined
    ): boolean => {
      if (!list) return false;
      return list.find((x) => x.id === id) !== undefined;
    };

    const filterValueChangeEvent = (e) => this.filterValueChanged();

    //local sub template generator
    const listSelect = (
      input: string,
      label: string,
      items: Array<SearchItem> | null | undefined,
      isSelected?: (id: string) => boolean
    ): TemplateResult => {
      let clearEvent = async (e) => {
        await this.clearSelect(this.$().find(`#input-${input}`));
        this.searchPage = this.updateSearchPage();
        await this.render();
      };
      return html` <div class="input-group input-group-sm mb-1">
        <button
          class="btn btn-outline-secondary"
          @click=${clearEvent}
          type="button"
          id="button-addon2"
        >
          <i class="fas fa-times"></i>
        </button>
        <select
          class="form-select use-select2"
          id=${`input-${input}`}
          data-placeholder=${label}
          mutiple
          aria-label=${label}
        >
          ${items?.map(
            (m) =>
              new Option(
                this.fallback(m.description, m.code),
                m.id,
                false,
                isSelected && isSelected(m.id)
              )
          )}
        </select>
      </div>`;
    };

    const stateTemplate = listSelect(
      'state',
      'State',
      filters?.addressState,
      (id) => containsId(id, lookups?.addressState)
    );
    const manufacturerTemplate = listSelect(
      'manufacturer',
      'Manufacturers',
      manufacturerList,
      (id) => containsId(id, lookups?.manufacturer)
    );
    const fabricatorTemplate = listSelect(
      'fabricator',
      'Fabricators',
      fabricatorList,
      (id) => containsId(id, lookups?.fabricator)
    );
    const operatingTypeTemplate = listSelect(
      'operatingtype',
      'Operating Type',
      filters?.operatingType,
      (id) => containsId(id, lookups?.operatingType)
    );
    const frameMaterialTemplate = listSelect(
      'framematerial',
      'Frame Material',
      filters?.frameMaterial,
      (id) => containsId(id, lookups?.frameMaterial)
    );
    const frameTypeTemplate = listSelect(
      'frametype',
      'Frame Type',
      filters?.frameType,
      (id) => containsId(id, lookups?.frameType)
    );
    const glazingSupplierTemplate = listSelect(
      'glazingsupplier',
      'Glass Supplier',
      filters?.glazingSupplier,
      (id) => containsId(id, lookups?.glazingSupplier)
    );
    const glassTypeTemplate = listSelect(
      'glasstype',
      'Glass Type',
      filters?.glassType,
      (id) => containsId(id, lookups?.glassType)
    );
    const glassSpecificationTemplate = listSelect(
      'glassspecification',
      'Glass Specification',
      filters?.glassSpecification,
      (id) => containsId(id, lookups?.glassSpecification)
    );
    const toggleSearchDraw = () => {
      this.searchDrawOpen = !this.searchDrawOpen;
      this.render();
    };

    /*
        const switchAutoRun = (e) => {
            this.runSearchImmediatly = e.target.checked;
        };
        */
    const runTabbedSearch = async (e) => await this.filterValueChanged();

    const addManufacturerAggregate = () => {
      if (this.graphSearchPage?.input?.lookups?.manufacturer?.length ?? 0 > 0) {
        return html`<option value="manufacturer">Manufacturer</option>`;
      }
      return html``;
    };

    const addGlassSupplierAggregate = () => {
      const distinctData = [
        ...new Set(
          this.graphSearchPage?.result?.results.map(
            (item) => item.glassManufacturer
          )
        ),
      ];
      if (distinctData.length > 0 && distinctData.length < 12) {
        return html`<option value="glassManufacturer">Glass Supplier</option>`;
      }
      return html``;
    };

    let windowIdFilterValue = lookups?.windowId ?? '';
    let descriptionFilterValue = lookups?.frameDescription ?? '';

    const setActiveTab = async (tabName: string) => {
      if (this.activeTab !== tabName) {
        this.activeTab = tabName;
        sessionStorage.setItem(activeTabSetting, tabName);
        this.render();
        await this.filterValueChanged();
      }
    };

    const getGraphResultsSummaryTemplate = () => {
      if (this.graphSearchPage?.result) {
        const result = this.graphSearchPage?.result;
        if (result.recordCountOnServer == result.results.length)
          return html`<b>displaying ${result.recordCountOnServer} results</b>`;
        else
          return html` displaying the first
            <b>${result.results.length}</b> results of a total of
            <b>${result.recordCountOnServer}</b>.
            <span class="text-danger"
              >Please refine your search to reduce results</span
            >`;
      } else return html``;
    };

    let sidebarTemplate = (allowSidebar: boolean): TemplateResult => {
      let sidebarClasses =
        'sidebar ' +
        (allowSidebar && this.searchDrawOpen
          ? ' sidebar-open'
          : 'sidebar-closed');
      let resetFilterEvent = (e) => {
        this.resetFilter();
        runTabbedSearch(e);
      };
      const eventRunSearch = (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          runTabbedSearch(e);
        }
      };

      return html` <div id=${elemId('sidebar')} class=${sidebarClasses}>
        <div class="sidebar-filters">
          <div class="row mb-2">
            <div class="col-12">
              <label for="input-windowId" class="form-label mx-2 my-0"
                >Window ID Starts with</label
              >
              <input
                id="input-windowId"
                type="search"
                @keyup=${eventRunSearch}
                .value=${windowIdFilterValue}
              />
            </div>
          </div>
          <div class="row mb-2">
            <div class="col-12">
              <label
                for="input-frameDescriptionFilter"
                class="form-label mx-2 my-0"
                >Frame Description Keyword Search</label
              >
              <input
                id="input-frameDescriptionFilter"
                type="search"
                @keyup=${eventRunSearch}
                .value=${descriptionFilterValue}
              />
            </div>
          </div>
          <div class="row mb-2">
            <div class="col-12">
              <label
                for="input-min-uvalue"
                @keyup=${eventRunSearch}
                class="form-label"
                >U-Value Min</label
              >
              <input
                id="input-min-uvalue"
                @keyup=${eventRunSearch}
                type="number"
                min="0"
                max="7"
                .value=${this.searchPage?.input.lookups?.minUw}
              />
              <label
                for="input-max-uvalue"
                @keyup=${eventRunSearch}
                class="form-label"
                >Max</label
              >
              <input
                id="input-max-uvalue"
                @keyup=${eventRunSearch}
                type="number"
                min="0"
                max="7"
                .value=${this.searchPage?.input.lookups?.maxUw}
              />
              <div class="form-text">
                please provide a number from <b>0 to 7</b> to filter your
                search, or leave blank
              </div>
            </div>
          </div>
          <div class="row mb-2">
            <div class="col-12">
              <label for="input-shgc" class="form-label">SHGCw</label>
              <div class="input-group shgc-group mb-3">
                <input
                  id="input-shgc"
                  @keyup=${eventRunSearch}
                  type="number"
                  min="0"
                  max="1"
                  class="form-control"
                  placeholder="SHGCw Value"
                  aria-label="SHGCw Value"
                  .value=${this.searchPage?.input.lookups?.shgc}
                />
                <span class="input-group-text">%-</span>
                <input
                  id="input-shgc-perc-minus"
                  @keyup=${eventRunSearch}
                  type="number"
                  class="form-control"
                  aria-label="minus percentage"
                  min="0"
                  max="100"
                  .value=${this.searchPage?.input.lookups?.minusSHGCPercentage}
                />
                <span class="input-group-text">%+</span>
                <input
                  id="input-shgc-perc-plus"
                  type="number"
                  @keyup=${eventRunSearch}
                  class="form-control"
                  aria-label="plus percentage"
                  min="0"
                  max="100"
                  .value=${this.searchPage?.input.lookups?.plusSHGCPercentage}
                />
              </div>
              <div class="form-text">
                please provide an SGHC number from <b>0 - 1</b> along with a %
                range, or leave blank
              </div>
            </div>
          </div>
          <div class="row mb-2">
            <div class="col-12">
              <label for="input-min-tvw" class="form-label">Minimum Tvw</label>
              <input
                id="input-min-tvw"
                @keyup=${eventRunSearch}
                type="number"
                min="0"
                max="1"
                .value=${this.searchPage?.input.lookups?.minTvw}
              />
              <div class="form-text">
                please provide a Tvw from <b>0 to 1</b> to filter, or leave
                blank to ignore
              </div>
            </div>
          </div>
        </div>
        <div class="state-filter">
          <div class="row">
            <div class="col-12">
              <div class="my-2">
                Filter Manufacturer and Fabricator by State
              </div>
              ${stateTemplate}
            </div>
          </div>
          <div class="row">
            <div class="col-12">${manufacturerTemplate}</div>
          </div>
          <div class="row">
            <div class="col-12">${fabricatorTemplate}</div>
          </div>
          <div class="row">
            <div class="col-12">${glazingSupplierTemplate}</div>
          </div>
          <div class="row">
            <div class="col-12">${frameTypeTemplate}</div>
          </div>
          <div class="row">
            <div class="col-12">${glassTypeTemplate}</div>
          </div>
          <div class="row">
            <div class="col-12">${glassSpecificationTemplate}</div>
          </div>
          <div class="row">
            <div class="col-12">${operatingTypeTemplate}</div>
          </div>
          <div class="row">
            <div class="col-12">${frameMaterialTemplate}</div>
          </div>
        </div>
        <div class="button-section">
          <div class="row mb-2">
            <div class="col">
              <button
                data-bs-toggle="collapse"
                data-bs-target="#section-filter"
                class="btn btn-sm btn-primary"
                @click=${runTabbedSearch}
              >
                Search
              </button>
              <button
                class="btn btn-sm btn-secondary"
                @click=${resetFilterEvent}
              >
                Clear Search
              </button>
            </div>
          </div>
        </div>
      </div>`;
    };

    let showSideBar =
      this.activeTab === tabs.graph || this.activeTab === tabs.table;

    let buttonsForDataPagesTemplate = (allowSidebar: boolean) => {
      if (!allowSidebar) return html``;
      return html`
        <div
          class="search-filters my-3 p-2 d-flex flex-row justify-content-between"
        >
          <div class="search-filters-buttons">
            <button
              id="sidebarCollapse"
              @click=${() => toggleSearchDraw()}
              class="btn btn-secondary btn-sm"
            >
              <i class="fas fa-align-left"></i>
              <span
                >${this.searchDrawOpen ? 'Close ' : 'Open '}Search Filters</span
              >
            </button>
            <button
              class="btn btn-secondary btn-sm"
              @click=${(e) => this.performCSV()}
            >
              Download CSV
            </button>
          </div>
        </div>
      `;
    };

    const contentClasses =
      'page-content ' +
      (this.searchDrawOpen && showSideBar ? ' content-open' : 'content-closed');
    return html`
      <div id=${this.elementId}>
        ${sidebarTemplate(showSideBar)}
        <div id=${elemId('content')} class=${contentClasses}>
          <div class="container-fluid mb-3">
            ${header()} ${buttonsForDataPagesTemplate(showSideBar)}
            <div
              class="spinner-border text-secondary visually-hidden"
              role="status"
            >
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>

          <div class="container-fluid">
            <ul
              class="nav nav-tabs"
              id=${elemId('search-page-tab')}
              role="tablist"
            >
              <li class="nav-item" role="presentation">
                <button
                  class=${'nav-link ' +
                  (this.activeTab === tabs.table ? ' active' : '')}
                  id=${elemId(tabs.table)}
                  @click=${() => setActiveTab(tabs.table)}
                  type="button"
                  role="tab"
                  aria-controls=${elemId('table-data')}
                  aria-selected="true"
                >
                  Table View
                </button>
              </li>
              <li class="nav-item" role="presentation">
                <button
                  class=${'nav-link ' +
                  (this.activeTab === tabs.graph ? ' active' : '')}
                  id=${elemId(tabs.graph)}
                  @click=${() => setActiveTab(tabs.graph)}
                  type="button"
                  role="tab"
                  aria-controls=${elemId('graph-data')}
                  aria-selected="true"
                >
                  Graph View
                </button>
              </li>
              <li class="nav-item" role="presentation">
                <button
                  class=${'nav-link ' +
                  (this.activeTab === tabs.favourites ? ' active' : '')}
                  id=${elemId(tabs.favourites)}
                  @click=${() => setActiveTab(tabs.favourites)}
                  type="button"
                  role="tab"
                  aria-controls=${elemId('fav-data')}
                  aria-selected="true"
                >
                  Favourites
                </button>
              </li>
              <!--
                        <li class="nav-item" role="presentation">
                            <button class=${'nav-link ' +
              (this.activeTab === tabs.job ? ' active' : '')} id=${elemId(
                tabs.job
              )}
                                @click=${() => setActiveTab(tabs.job)}
                                type="button"
                                role="tab"
                                aria-controls=${elemId(
                'job-data'
              )} aria-selected="true">@Calculations</button>
                                </li>
                                                      -->
              <li class="nav-item" role="presentation">
                <button
                  class=${'nav-link ' +
                  (this.activeTab === tabs.basicjob ? ' active' : '')}
                  id=${elemId(tabs.basicjob)}
                  @click=${() => setActiveTab(tabs.basicjob)}
                  type="button"
                  role="tab"
                  aria-controls=${elemId('basicjob-data')}
                  aria-selected="true"
                >
                  Job
                </button>
              </li>
            </ul>
            <div class="tab-content" id=${elemId('search-page-tab-content')}>
              <div
                class=${'tab-pane ' +
                (this.activeTab == tabs.table ? ' show active' : '')}
                id=${elemId('table-data')}
                role="tabpanel"
                aria-labelledby=${elemId(tabs.table)}
              >
                <div class="table-full hidden">
                  <table
                    class="data-table table cell-border table-striped"
                    style="width:1720px"
                  >
                    <thead></thead>
                    <tbody></tbody>
                  </table>
                </div>
                <div class="table-empty">
                  <p class="table-empty-text">
                    Please Enter at least one value in the search filters to
                    find products
                  </p>
                  <img src="/wers/images/WERSLogo.png" />
                </div>
              </div>
              <div
                class=${'tab-pane ' +
                (this.activeTab == tabs.favourites ? ' show active' : '')}
                id=${elemId('fav-data')}
                role="tabpanel"
                aria-labelledby=${elemId(tabs.favourites)}
              >
                ${this.favouritesTemplate()}
              </div>
              <div
                class=${'tab-pane ' +
                (this.activeTab == tabs.job ? ' show active' : '')}
                id=${elemId('job-data')}
                role="tabpanel"
                aria-labelledby=${elemId(tabs.job)}
              >
                ${this.jobTemplate()}
              </div>
              <div
                class=${'tab-pane ' +
                (this.activeTab == tabs.basicjob ? ' show active' : '')}
                id=${elemId('basicjob-data')}
                role="tabpanel"
                aria-labelledby=${elemId(tabs.basicjob)}
              >
                ${this.basicJobTemplate()}
              </div>
              <div
                class=${'tab-pane ' +
                (this.activeTab == tabs.graph ? ' show active' : '')}
                id=${elemId('graph-data')}
                role="tabpanel"
                aria-labelledby=${elemId(tabs.graph)}
              >
                <div class="container">
                  <div class="row">
                    <div class="col-8">
                      <div class="">
                        <canvas
                          id=${'chart-' + this.uiid}
                          class="chart"
                          width="600"
                          height="500"
                        ></canvas>
                      </div>
                      <div class="">
                        <div class="row mt-3 gx-5">
                          <div class="col-4">
                            <div class="form-floating">
                              <select
                                .value=${this.graphAggregateValue}
                                class="form-select"
                                style="width:250px;"
                                id="graph-data-aggregate"
                                @change=${(e) =>
                                  this.changeGraphAggregate(e.target.value)}
                                aria-label="aggregate graph data"
                              >
                                <option value="frameMaterial">
                                  Frame Material
                                </option>
                                <option value="frameType">Frame Type</option>
                                <option value="operatingType">
                                  Operating Type
                                </option>
                                <option value="glassSpecification">
                                  Glass Specification
                                </option>
                                <option value="glassType">Glass Type</option>
                                ${addManufacturerAggregate()}
                                ${addGlassSupplierAggregate()}
                              </select>
                              <label for="graph-data-aggregate"
                                >Choose data aggregate</label
                              >
                            </div>
                          </div>
                          <div class="col-8">
                            ${getGraphResultsSummaryTemplate()}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="col-4">
                      <h5>
                        Selected Product
                        <a
                          class="text-warning"
                          @click=${() => this.addCurrentGraphItemToFavourites()}
                          data-bs-toggle="tooltip"
                          data-bs-placement="top"
                          title="Add To Favourites"
                          ><i
                            class=${(this.currentGraphItemIsFavourite()
                              ? 'fas'
                              : 'far') + ' fa-star'}
                          ></i
                        ></a>
                        <a
                          @click=${() => this.addcurrentGraphItemToJob()}
                          data-bs-toggle="tooltip"
                          data-bs-placement="top"
                          title="Add To Job"
                          ><i
                            class=${(this.currentGraphItemIsInJob()
                              ? 'text-success'
                              : 'text-secondary') + ' fas fa-home'}
                          ></i
                        ></a>
                      </h5>
                      ${this.currentGraphItemTemplate()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }
  searchByFrameDescription(): unknown {
    throw new Error('Method not implemented.');
  }
  addcurrentGraphItemToJob() {
    if (!this.currentGraphItem) return;
    this.basicJobItemView.toggleJob(this.currentGraphItem);
    this.scatterChart.update();
    this.render();
  }
  currentGraphItemIsInJob(): boolean {
    if (this.currentGraphItem?.windowId)
      return this.isJob(this.currentGraphItem?.windowId);
    else return false;
  }
  private activePageLookups(elemId: (name: string) => string) {
    return this.activeTab === tabs.favourites
      ? null
      : this.activeTab === tabs.graph
      ? this.graphSearchPage?.input.lookups
      : this.searchPage?.input.lookups;
  }

  favouritesTemplate(): TemplateResult {
    if (this.activeTab === tabs.favourites) {
      return this.favouritesView.template();
    } else return html``;
  }
  jobTemplate(): TemplateResult {
    if (this.activeTab === tabs.job) {
      return this.jobItemView.template();
    } else return html``;
  }
  basicJobTemplate(): TemplateResult {
    if (this.activeTab === tabs.basicjob) {
      return this.basicJobItemView.template();
    } else return html``;
  }
  addCurrentGraphItemToFavourites() {
    if (!this.currentGraphItem) return;
    this.favouritesView.toggleFavourite(this.currentGraphItem);
    this.graphData.datasets[1].data = this.favouritesAsGraphData();
    this.scatterChart.update();

    //this.favouritesView.setFavourites([...this.favouritesView.favourites, { displayRow: this.currentGraphItem }]);
  }

  isFavourite(windowId: string): boolean {
    return this.favouritesView.isFavourite(windowId);
  }

  currentGraphItemIsFavourite(): boolean {
    if (this.currentGraphItem?.windowId)
      return this.isFavourite(this.currentGraphItem?.windowId);
    else return false;
  }
  currentGraphItemTemplate(): TemplateResult {
    const item = this.currentGraphItem;
    return html`
      <table class="table table-striped table-bordered table-sm">
        <tbody>
          <tr>
            <th scope="row">Window ID</th>
            <td>${item?.windowId ?? 'Click an item on the graph'}</td>
          </tr>
          <tr>
            <th scope="row">Rating</th>
            <td>
              <wers-stars
                .value="${item?.coolingStars ?? 0}"
                type="cool"
              ></wers-stars>
              <wers-stars
                .value="${item?.heatingStars ?? 0}"
                type="heat"
              ></wers-stars>
            </td>
          </tr>
          <tr>
            <th scope="row">Manufacturer</th>
            <td>${item?.manufacturer ?? ''}</td>
          </tr>
          <tr>
            <th scope="row">Frame Description</th>
            <td>${item?.frameDescription ?? ''}</td>
          </tr>
          <tr>
            <th scope="row">Frame Type</th>
            <td>${item?.frameType ?? ''}</td>
          </tr>
          <tr>
            <th scope="row">Operating Type</th>
            <td>${item?.operatingType ?? ''}</td>
          </tr>
          <tr>
            <th scope="row">Glass Supplier</th>
            <td>${item?.glassManufacturer ?? ''}</td>
          </tr>
          <tr>
            <th scope="row">Glass</th>
            <td>${item?.glazingDisplayName ?? ''}</td>
          </tr>
          <tr>
            <th scope="row">Glazing</th>
            <td>${item?.glassType ?? ''}</td>
          </tr>
          <tr>
            <th scope="row">U-Value</th>
            <td>${item?.uw?.toFixed(1) ?? ''}</td>
          </tr>
          <tr>
            <th scope="row">SHGCw</th>
            <td>${item?.shgc?.toFixed(2) ?? ''}</td>
          </tr>
          <tr>
            <th scope="row">Tvw</th>
            <td>${item?.tvw ?? ''}</td>
          </tr>
          <tr>
            <th scope="row">Ai</th>
            <td>${item?.ai ?? ''}</td>
          </tr>
          <tr>
            <th scope="row">Cooling %</th>
            <td>${item?.coolingPercentage ?? ''}</td>
          </tr>
          <tr>
            <th scope="row">Heating %</th>
            <td>${item?.heatingPercentage ?? ''}</td>
          </tr>
        </tbody>
      </table>
    `;
  }
  async changeGraphAggregate(value: string) {
    this.graphAggregateValue = value;
    this.graphData = await this.buildScatterGraphData();
    if (this.scatterChart) {
      this.scatterChart.data.datasets = this.graphData.datasets;
      this.scatterChart.update();
      return;
    }
  }
  async filterValueChanged() {
    this.changeEvent++;
    try {
      if (this.changeEvent > 1) return;
      await this.runTabbedSearch();
    } finally {
      this.changeEvent--;
    }
  }
  async runTabbedSearch() {
    if (this.activeTab === tabs.graph) {
      await this.performGraphSearch();
    } else if (this.activeTab === tabs.table) {
      await new Promise<void>((resolve) => {
        if (this.dataTable) this.dataTable.ajax.reload(resolve, true);
        else resolve();
      });
      //            await this.performSearch(0);
    }
  }

  async performCSV() {
    this.searchPage = this.createSearchPage(0);
    this.$().find('.spinner-border').removeClass('visually-hidden');
    try {
      const csv = await runSimulationCSV(this.searchPage.input);
      this.download(csv, 'wers-search-result.csv', 'text/csv');
    } finally {
      this.$().find('.spinner-border').addClass('visually-hidden');
    }
    saveSimulationQueryInput(this.searchPage?.input, this.manufacturerFilter);
    await this.render();
  }

  download(data, filename, type) {
    var file = new Blob([data], { type: type });
    var a = document.createElement('a'),
      url = URL.createObjectURL(file);
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(function () {
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }, 0);
  }

  async setPageIndex(index: any) {
    await this.performSearch(index);
  }

  private compareSearchPage(
    page: SimulationQueryInput,
    newPage: SimulationQueryInput | null | undefined
  ): Boolean {
    return JSON.stringify(page) === JSON.stringify(newPage);
  }
  async performSearch(pageIndex: number, pageSize?: number) {
    const newSearchPage = this.createSearchPage(
      pageIndex,
      pageSize ?? this.pageSize
    );

    if (
      this.compareSearchPage(newSearchPage.input, this.searchPage?.input) &&
      this.searchPage?.result
    ) {
      //no need to rerun.. we already have data
      return;
    }

    this.searchPage = newSearchPage;
    saveSimulationQueryInput(this.searchPage?.input, this.manufacturerFilter);
    this.$().find('.spinner-border').removeClass('visually-hidden');
    try {
      this.searchPage.result = await runSimulationSearch(this.searchPage.input);
      let hideTable = (this.searchPage.result?.results.length ?? 0) == 0;
      if (hideTable) $('.table-empty').removeClass('hidden');
      else $('.table-empty').addClass('hidden');
      if (hideTable) $('.table-full').addClass('hidden');
      else $('.table-full').removeClass('hidden');
    } finally {
      this.$().find('.spinner-border').addClass('visually-hidden');
    }
    saveSimulationQueryInput(this.searchPage?.input, this.manufacturerFilter);
    //await this.render();
  }

  async performGraphSearch(renderOnComplete = true) {
    const newSearchPage = this.createSearchPage(0, 5000);
    //graphical searching is limited to 5000 so lets take the best rated windows first.
    newSearchPage.input.sortField = 'uw';
    newSearchPage.input.sortAsc = true;
    if (
      this.compareSearchPage(
        newSearchPage.input,
        this.graphSearchPage?.input
      ) &&
      this.graphSearchPage?.result
    ) {
      //no need to rerun.. we already have data
      return;
    }
    this.graphSearchPage = newSearchPage;
    saveSimulationQueryInput(
      this.graphSearchPage?.input,
      this.manufacturerFilter
    );
    this.$().find('.spinner-border').removeClass('visually-hidden');
    try {
      this.graphSearchPage.result = await runSimulationSearch(
        this.graphSearchPage.input
      );
    } finally {
      this.$().find('.spinner-border').addClass('visually-hidden');
    }
    saveSimulationQueryInput(
      this.graphSearchPage?.input,
      this.manufacturerFilter
    );
    if (renderOnComplete) {
      this.rebuildGraphData = true;
      await this.render();
    }
  }

  getListValues(parent: JQuery<HTMLElement>, name: string): SearchItemBase[] {
    const values = (parent.find(`#input-${name}`) as any).select2('val');
    //this should not occur in a proper setup, but fine during development when things are inprogress
    if (!values) return [];
    return (values as string[]).map((v) => {
      let item: SearchItemBase = { id: v };
      return item;
    });
  }
  setListValues(
    parent: JQuery<HTMLElement>,
    name: string,
    selection: SearchItemBase[]
  ) {
    (parent.find(`#input-${name}`) as any)
      .val(selection.map((x) => x.id))
      .change();
  }

  createSearchPage(pageIndex: number, pageSize?: number): SimulationSearchPage {
    this.currentPageIndex = pageIndex ?? 0;
    const jthis = this.$();
    console.log('search page for ' + this.elementId);
    if (jthis.length === 0) console.log('could not find element parent');
    const getStrValue = (name: string): string | null => {
      const $input = jthis.find('#input-' + name);
      if ($input.length === 0) return null;
      const input = $input[0] as HTMLInputElement;
      if (!input.value || input.value === '') return null;
      return input.value;
    };

    const getValue = (name: string) => {
      const value = getStrValue(name);
      if (!value || value === '') return null;
      return parseFloat(value);
    };

    let result: SimulationSearchPage = {
      input: {
        pageIndex: this.currentPageIndex,
        pageSize: pageSize ?? this.pageSize,
        sortField: this.sortField,
        sortAsc: this.sortAsc,
        manufacturerFilter: this.manufacturerFilter ?? 1,
        lookups: {
          fabricator: this.getListValues(jthis, 'fabricator'),
          frameType: this.getListValues(jthis, 'frametype'),
          frameMaterial: this.getListValues(jthis, 'framematerial'),
          glassSpecification: this.getListValues(jthis, 'glassspecification'),
          glassType: this.getListValues(jthis, 'glasstype'),
          glazingSupplier: this.getListValues(jthis, 'glazingsupplier'),
          manufacturer: this.getListValues(jthis, 'manufacturer'),
          operatingType: this.getListValues(jthis, 'operatingtype'),
          addressState: this.getListValues(jthis, 'state'),
          minHeatingStar: null,
          minCoolingStar: null,
          maxUw: getValue('max-uvalue'),
          minUw: getValue('min-uvalue'),
          shgc: getValue('shgc'),
          plusSHGCPercentage: getValue('shgc-perc-plus'),
          minusSHGCPercentage: getValue('shgc-perc-minus'),
          minTvw: getValue('min-tvw'),
          windowId: getStrValue('windowId'),
          frameDescription: getStrValue('frameDescriptionFilter'),
        },
      },
    };
    return result;
  }

  updateSearchPage(): SimulationSearchPage {
    const jthis = this.$();
    console.log('search page for ' + this.elementId);
    if (jthis.length === 0) console.log('could not find element parent');
    const getStrValue = (name: string): string | null => {
      const $input = jthis.find('#input-' + name);
      if ($input.length === 0) return null;
      const input = $input[0] as HTMLInputElement;
      if (!input.value || input.value === '') return null;
      return input.value;
    };

    const getValue = (name: string) => {
      const value = getStrValue(name);
      if (!value || value === '') return null;
      return parseFloat(value);
    };

    let result: SimulationSearchPage = {
      input: {
        pageIndex: this.searchPage?.input.pageIndex ?? 0,
        pageSize: this.searchPage?.input.pageSize ?? 0,
        sortField: this.sortField,
        sortAsc: this.sortAsc,
        manufacturerFilter: this.manufacturerFilter ?? 1,
        lookups: {
          fabricator: this.getListValues(jthis, 'fabricator'),
          frameType: this.getListValues(jthis, 'frametype'),
          frameMaterial: this.getListValues(jthis, 'framematerial'),
          glassSpecification: this.getListValues(jthis, 'glassspecification'),
          glassType: this.getListValues(jthis, 'glasstype'),
          glazingSupplier: this.getListValues(jthis, 'glazingsupplier'),
          manufacturer: this.getListValues(jthis, 'manufacturer'),
          operatingType: this.getListValues(jthis, 'operatingtype'),
          addressState: this.getListValues(jthis, 'state'),
          minHeatingStar: null,
          minCoolingStar: null,
          maxUw: getValue('max-uvalue'),
          minUw: getValue('min-uvalue'),
          shgc: getValue('shgc'),
          plusSHGCPercentage: getValue('shgc-perc-plus'),
          minusSHGCPercentage: getValue('shgc-perc-minus'),
          minTvw: getValue('min-tvw'),
          windowId: getStrValue('windowId'),
          frameDescription: getStrValue('frameDescriptionFilter'),
        },
      },
    };
    return result;
  }

  async clearSelect(arg0: HTMLSelectElement) {
    this.changeEvent++;
    ($(arg0) as any).select2('val', '-1');
    this.changeEvent--;
  }

  protected $(): any {
    return $(`#${this.elementId}`);
  }
  protected async onAfterRender() {
    const jthis = this.$();
    this.changeEvent++;
    try {
      jthis
        .find('.use-select2')
        .each((index: number, elem: HTMLSelectElement) => {
          this.select2(elem, { multiple: true });
          this.clearSelect(elem);
          $(elem).on('change', this.select2ChangeEvent);
        });
      const lookups = this.activeLookups();
      if (lookups) {
        this.setListValues(jthis, 'fabricator', lookups.fabricator);
        this.setListValues(jthis, 'frametype', lookups.frameType);
        this.setListValues(jthis, 'framematerial', lookups.frameMaterial);
        this.setListValues(
          jthis,
          'glassspecification',
          lookups.glassSpecification
        );
        this.setListValues(jthis, 'glasstype', lookups.glassType);
        this.setListValues(jthis, 'glazingsupplier', lookups.glazingSupplier);
        this.setListValues(jthis, 'manufacturer', lookups.manufacturer);
        this.setListValues(jthis, 'operatingtype', lookups.operatingType);
        this.setListValues(jthis, 'state', lookups.addressState);
      }

      if (this.activeTab == tabs.graph)
        await this.initializeScatterChart(jthis);
      if (this.activeTab == tabs.table) await this.initializeDataTable(jthis);
      if (this.activeTab == tabs.favourites) this.favouritesView.afterRender();
      if (this.activeTab == tabs.job) this.jobItemView.afterRender();
      if (this.activeTab == tabs.basicjob) this.basicJobItemView.afterRender();
    } finally {
      this.changeEvent--;
    }
  }

  private activeLookups(): SearchParameterFilter | undefined {
    return this.activeTab === tabs.graph
      ? this.graphSearchPage?.input.lookups
      : this.searchPage?.input.lookups;
  }

  private async initializeScatterChart(jthis: any): Promise<void> {
    if (jthis.length === 0) {
      //first load problem
      setTimeout(() => {
        this.initializeScatterChart(this.$());
      }, 250);
      return;
    }
    if (!this.rebuildGraphData) return;
    this.rebuildGraphData = false;
    this.graphData = await this.buildScatterGraphData();
    if (this.scatterChart) {
      this.scatterChart.data.datasets = this.graphData.datasets;
      this.graphData.datasets[1].data = this.favouritesAsGraphData();
      this.scatterChart.update();
      return;
    }
    let chartElement = jthis.find('#chart-' + this.uiid);
    this.scatterChart = new globalThis.Chart(chartElement, {
      type: 'scatter',
      data: this.graphData,
      options: {
        onClick: (evt) => {
          const points = evt.chart.getElementsAtEventForMode(
            evt,
            'nearest',
            { intersect: true },
            true
          );
          if (points.length) {
            const firstPoint = points[0];
            const label = evt.chart.data.labels[firstPoint.index];
            const value =
              evt.chart.data.datasets[firstPoint.datasetIndex].data[
                firstPoint.index
              ];
            this.graphData.datasets[0].data = [
              { x: value.item.uw, y: value.item.shgc, item: value.item },
            ];
            this.currentGraphItem = value.item;
            this.scatterChart.update();
            this.render();
          }
        },
        elements: {
          point: {
            hoverRadius: 10,
            hoverBackgroundColor: 'magenta',
          },
        },
        hover: { mode: null },
        maintainAspectRatio: true,
        responsive: false,
        animation: false,
        plugins: {
          title: {
            display: true,
            text: 'SHGCw/Uw Chart',
          },
          tooltip: {
            enabled: false,
          },
          zoom: {
            zoom: {
              wheel: {
                enabled: true,
              },
              pinch: {
                enabled: true,
              },
              mode: 'xy',
            },
          },
          legend: {
            position: 'top',
            labels: {
              filter: function (item, chart) {
                // Logic to remove a particular legend item goes here
                return !(item.text === '');
              },
            },
          },
        },
        scales: {
          x: {
            display: true,
            title: {
              display: true,
              text: 'Uw (UValue)',
            },
            ticks: {
              stepSize: 1,
            },
            min: 0,
            max: 7,
          },
          y: {
            display: true,
            title: {
              display: true,
              text: 'SGHC',
            },
            ticks: {
              stepSize: 0.1,
            },
            min: 0,
            max: 1,
          },
        },
      },
    });

    function itemfor(context: any): DisplayRow {
      return context.dataset.data[context.dataIndex].item as DisplayRow;
    }
  }
  focusDataset(legendItem: any, legend: any) {
    console.log(legendItem);
    (legend.chart.data.datasets as Array<any>).forEach((dataset, index) => {
      if (!legendItem) {
        dataset.backgroundColor = scattergraphBackgroundColors[index];
        legend.chart.setDatasetVisibility(index, true);
      } else {
        if (index == legendItem.datasetIndex) {
          dataset.backgroundColor = scattergraphBackgroundColors[index];
          legend.chart.setDatasetVisibility(index, true);
        } else {
          dataset.backgroundColor = 'azure';
          legend.chart.setDatasetVisibility(index, false);
        }
      }
    });
    legend.chart.update();
  }

  private async buildScatterGraphData(): Promise<any> {
    const dataAggregate = this.graphAggregateValue ?? 'frameMaterial';

    const dataAggregateIndex: string[] = [];
    const dataSets: ScatterGraphData[] = [
      {
        label: '',
        data: [],
        pointStyle: 'circle',
        pointRadius: 10,
        borderWidth: 3,
        pointBorderColor: 'yellow',
        backgroundColor: 'blue',
      },
      {
        label: '',
        data: this.favouritesAsGraphData(),
        pointStyle: 'triangle',
        pointRadius: 8,
        borderWidth: 2,
        pointBorderColor: 'black',
        backgroundColor: 'orange',
      },
    ];

    const filters = (await getSearchParameters())?.cache;
    if (!this.graphSearchPage?.result) await this.performGraphSearch(false);
    const nathersMan = filters?.manufacturer.find(
      (x) => x.id == '1133' /* "NATHERS DEFAULT" */
    );
    let results = this.graphSearchPage?.result?.results ?? [];
    const hasNathersFilter =
      this.graphSearchPage?.input.lookups?.manufacturer.find(
        (x) => x.id == nathersMan?.id
      ) != undefined;
    results = results.filter(
      (x) => hasNathersFilter || x.manufacturer != nathersMan?.description
    );

    const legend = [...new Set(results?.map((item) => item[dataAggregate]))];
    legend.forEach((item) => {
      dataAggregateIndex.push(item);
      dataSets.push({
        label: `${item}`,
        data: [],
        backgroundColor: scattergraphBackgroundColors[dataSets.length - 2],
      });
    });

    //push all the data into the aggregations
    results.forEach((row) => {
      const datasetIndex = dataAggregateIndex.indexOf(row[dataAggregate]) + 2;
      dataSets[datasetIndex].data.push({ x: row.uw, y: row.shgc, item: row });
    });

    return {
      datasets: dataSets,
    };
  }

  private favouritesAsGraphData(): ScatterPoint[] {
    return this.favouritesView.favourites.map((row) => {
      return {
        x: row.displayRow.uw,
        y: row.displayRow.shgc,
        item: row.displayRow,
      };
    });
  }

  private async initializeDataTable(jthis: any): Promise<void> {
    const dataset = this.searchPage?.result?.results ?? [];
    const $dataTable = jthis.find('.data-table') as any;
    const dataTableInitialized = $j.dataTable.isDataTable($dataTable);

    if (!dataTableInitialized) {
      const columns = [
        {
          title: '<i class="fas fa-star"></i>',
          width: '40px',
          data: 'windowId',
          render: (value) => {
            const favClassVal =
              (this.isFavourite(value) ? 'fas ' : 'far ') +
              ' fa-star text-warning favourite ';
            const jobClassVal =
              (this.isJob(value) ? 'text-success ' : 'text-secondary ') +
              ' fas fa-home  job';

            const val = `<i class="${favClassVal}" ></i> <i class="${jobClassVal}"  ></i>`;
            return val;
          },
        },
        {
          title: 'Window ID',
          width: '120px',
          data: 'windowId',
        },
        {
          title: 'Manufacturer',
          width: '150px',
          data: 'manufacturer',
        },
        {
          title: 'Frame Description',
          width: '350px',
          data: 'frameDescription',
        },
        {
          title: 'Glass',
          width: '160px',
          data: 'glazingDisplayName',
          render: function (value, _type, row) {
            return `<span data-bs-toggle="tooltip" data-bs-placement="top" title="${row.glazingLongDisplayName}">${value}</span>`;
          },
        },
        {
          title: 'Glass Supplier',
          width: '150px',
          data: 'glassManufacturer',
        },
        {
          title: 'Uw',
          width: '40px',
          data: 'uw',
          render: function (value) {
            return value.toFixed(1);
          },
        },
        {
          title: 'SHGCw',
          width: '40px',
          data: 'shgc',
          render: function (value) {
            return value.toFixed(2);
          },
        },
        {
          title: 'Tvw',
          width: '40px',
          data: 'tvw',
        },
        {
          title: 'Ai',
          width: '40px',
          data: 'ai',
        },
        {
          title: 'Operating Type',
          width: '150px',
          data: 'operatingType',
        },

        {
          title: 'Cooling',
          width: '120px',
          data: 'coolingStars',
          render: function (value) {
            return `<wers-stars value="${value}" type="cool"></wers-stars>`;
          },
        },
        {
          title: 'Heating',
          width: '120px',
          data: 'heatingStars',
          render: function (value) {
            return `<wers-stars value="${value}" type="heat"></wers-stars>`;
          },
        },
        {
          title: 'Cool %',
          data: 'coolingPercentage',
          width: '120px',
          render: function (value) {
            return `<span class="text-primary">${value}%</span>`;
          },
        },
        {
          title: 'Heat %',
          data: 'heatingPercentage',
          width: '120px',
          render: function (value) {
            return `<span class="text-danger">${value}%</span>`;
          },
        },
      ];
      this.dataTable = $dataTable.DataTable({
        scrollX: true,
        scrollY: '50vh',
        scrollCollapse: true,
        paging: true,
        order: [[0, 'asc']],
        pageLength: 200,
        bLengthChange: false,
        processing: true,
        serverSide: true,
        ajax: (request: any, drawCallback, settings) =>
          this.pageLoad(request, drawCallback, settings),
        //data: dataset,
        searching: false,
        info: true,
        columns: columns,
        fixedColumns: { left: 2 },
        autoWidth: false,
      });
      this.dataTable.columns.adjust();
      let table = this.dataTable;
      $dataTable.on('click', 'i', (ev: Event) => {
        if (ev.target !== null) {
          let row = this.dataTable.row(($(ev.target) as any).parents('tr'));
          let data = row.data();
          if (data) {
            if (
              (ev.currentTarget as HTMLElement).classList.contains('favourite')
            ) {
              this.favouritesView.toggleFavourite(data);
            } else if (
              (ev.currentTarget as HTMLElement).classList.contains('job')
            ) {
              this.basicJobItemView.toggleJob(data);
            }
            row.invalidate();
          }
        }
      });
    } else {
      this.dataTable = $dataTable.DataTable();
      this.dataTable.rows().invalidate();
    }
  }
  isJob(value: string) {
    return this.basicJobItemView.hasJobItem(value);
  }

  //take the datatables request requirements and ignore most of them that we dont care about and reprocess into a system we can use
  async pageLoad(request: any, drawCallback, settings) {
    if (request.order?.length > 0) {
      this.sortField =
        settings.aoColumns[request.order[0].column ?? 0].data ?? 'windowId';
      this.sortAsc = request.order[0].dir === 'asc';
    } else {
      this.sortField = 'windowId';
      this.sortAsc = true;
    }
    const pageIndex = request.start / request.length;
    await this.performSearch(pageIndex, request.length);
    drawCallback({
      draw: request.draw,
      recordsTotal: this.searchPage?.result?.recordCountOnServer,
      recordsFiltered: this.searchPage?.result?.recordCountOnServer,
      data: this.searchPage?.result?.results,
    });

    let tooltipTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="tooltip"]')
    );
    let tooltipList = tooltipTriggerList.map(function (tooltipTriggerEl) {
      return new globalThis.bootstrap.Tooltip(tooltipTriggerEl);
    });
  }

  async resetFilter() {
    this.searchPage = undefined;
    this.graphSearchPage = undefined;
    saveSimulationQueryInput(null, this.manufacturerFilter);

    const jthis = this.$();

    const clearValue = (name: string) => {
      const $input = jthis.find('#input-' + name);
      if ($input.length === 0) alert('unknown input');
      const input = $input[0] as HTMLInputElement;
      input.value = '';
    };
    const clearSelectValue = async (input) =>
      await this.clearSelect(this.$().find(`#input-${input}`));

    clearSelectValue('fabricator');
    clearSelectValue('frametype');
    clearSelectValue('framematerial');
    clearSelectValue('glassspecification');
    clearSelectValue('glasstype');
    clearSelectValue('glazingsupplier');
    clearSelectValue('manufacturer');
    clearSelectValue('operatingtype');
    clearSelectValue('state');

    clearValue('max-uvalue');
    clearValue('min-uvalue');
    clearValue('shgc');
    clearValue('shgc-perc-plus');
    clearValue('shgc-perc-minus');
    clearValue('min-tvw');
    clearValue('windowId');
    clearValue('frameDescriptionFilter');

    await this.render();
  }
}
